import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Event, EventByIdArgs, EventUserStatus, eventsApi } from '../services';
import {
  eventCancel,
  eventCancelParticipation,
  eventDelete,
  eventEdit,
  eventJoinAsParticipant,
  eventStatusUpdate,
  eventUpdateChannels,
} from './common';

interface EventDetailsModalState {
  isFetched: boolean;
  event?: Event;
}

const initialState: EventDetailsModalState = {
  isFetched: false,
  event: undefined,
};

export const getEventModal = createAsyncThunk(
  'getEventModal',
  async (args: EventByIdArgs, { dispatch }) => {
    return await dispatch(eventsApi.endpoints.eventById.initiate(args)).unwrap();
  }
);

const eventDetailsModalSlice = createSlice({
  name: 'eventDetailsModal',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getEventModal.pending, (state) => {
        state.isFetched = false;
        state.event = undefined;
      })
      .addCase(getEventModal.fulfilled, (state, action) => {
        state.isFetched = true;
        state.event = action.payload;
      })
      .addCase(eventJoinAsParticipant.fulfilled, (state, action) => {
        const { eventId, payload, userProfile } = action.payload;
        const { event } = state;

        if (!event || event.id !== eventId) {
          return;
        }

        if (payload === EventUserStatus.CONFIRMED) {
          event.participantCountConfirmed++;
          event.participantsPreview.push(userProfile);
        } else if (payload === EventUserStatus.WAITLISTED) {
          event.participantCountWaitlisted++;
        }

        event.participantStatus = payload;
      })
      .addCase(eventCancelParticipation.fulfilled, (state, action) => {
        const { eventId, payload, userProfile } = action.payload;
        const { event } = state;

        if (!event || event.id !== eventId || !payload) {
          return;
        }

        const { participantStatus } = event;

        if (participantStatus === EventUserStatus.CONFIRMED) {
          event.participantCountConfirmed--;
          event.participantsPreview = event.participantsPreview.filter(
            ({ id }) => id !== userProfile.id
          );
        } else if (participantStatus === EventUserStatus.WAITLISTED) {
          event.participantCountWaitlisted--;
        }

        event.participantStatus = null;
      })
      .addCase(eventCancel.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        const { event } = state;

        if (!event || event.id !== id) {
          return;
        }

        event.status = status;
        event.participantStatus = null;
        event.participantCountConfirmed = 0;
        event.participantCountWaitlisted = 0;
        event.participantsPreview = [];
      })
      .addCase(eventStatusUpdate.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        const { event } = state;

        if (!event || event.id !== id) {
          return;
        }

        event.status = status;
      })
      .addCase(eventEdit.fulfilled, (state, action) => {
        if (state.event?.id !== action.payload.id) {
          return;
        }

        state.event = action.payload;
      })
      .addCase(eventDelete.fulfilled, (state, action) => {
        const { deleted } = action.payload;

        if (!deleted) {
          return;
        }

        state.event = undefined;
      })
      .addCase(eventUpdateChannels.fulfilled, (state, action) => {
        const { id, channels } = action.payload;
        const { event } = state;

        if (!event || event.id !== id) {
          return;
        }

        event.channels = channels;
      });
  },
});

export default eventDetailsModalSlice.reducer;
